import { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/components/ToastProvider";
import { createInvite, listInvites, resendInvite } from "@/api/invites";
import type { InviteSummary } from "@/api/invites";
import { useSessionUser } from "@/hooks/useSessionUser";
import { Link, useLocation } from "react-router-dom";

const ROLE_OPTIONS = [
  { value: "member", label: "Member" },
  { value: "manager", label: "Manager" },
  { value: "artist", label: "Artist" },
  { value: "both", label: "Manager + Artist" },
] as const;

function formatDate(value?: string) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

export default function InvitesPage() {
  const { sessionUser, session } = useSessionUser();
  const { push } = useToast();
  const location = useLocation();
  const normalizedRole = (sessionUser?.role ?? session?.role ?? "").toLowerCase();
  const isAdmin = ["owner", "admin"].includes(normalizedRole);
  const isInvitesActive = location.pathname.startsWith("/control/master/invites");
  const isRegistryActive = location.pathname.startsWith("/control/master/registry");
  const isOverviewActive = !isInvitesActive && !isRegistryActive;
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<typeof ROLE_OPTIONS[number]["value"]>("member");
  const [orgId, setOrgId] = useState("");
  const [lastUrl, setLastUrl] = useState<string | null>(null);
  const [invites, setInvites] = useState<InviteSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const fetchInvites = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listInvites();
      setInvites(data);
    } catch (err) {
      push({ title: "Invites", message: err instanceof Error ? err.message : "Unable to load invites." });
    } finally {
      setLoading(false);
    }
  }, [push]);

  useEffect(() => {
    void fetchInvites();
  }, [fetchInvites]);

  const handleCreate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.trim()) return;
    setCreating(true);
    try {
      const response = await createInvite({ email: email.trim(), role, organization_id: orgId.trim() || undefined });
      setLastUrl(response.invite_url);
      const syntheticInvite: InviteSummary = {
        id: response.token,
        email: email.trim(),
        role,
        organization_id: undefined,
        invited_by_user_id: undefined,
        expires_at: response.expires_at,
        accepted_at: undefined,
        created_at: new Date().toISOString(),
        valid: true,
        token: response.token,
        invite_url: response.invite_url,
      };
      setInvites((prev) => [syntheticInvite, ...prev.filter((invite) => invite.id !== syntheticInvite.id)]);
      push({ title: "Invite created", message: "Copy and share the link below." });
      setEmail("");
      await fetchInvites();
    } catch (err) {
      push({ title: "Invite failed", message: err instanceof Error ? err.message : "Unable to create invite." });
    } finally {
      setCreating(false);
    }
  };

  const handleResend = async (inviteId: string) => {
    setResendingId(inviteId);
    try {
      const payload = await resendInvite(inviteId);
      push({ title: "Invite resent", message: "Copy the link from the notification." });
      setLastUrl(payload.invite_url);
    } catch (err) {
      push({ title: "Resend failed", message: err instanceof Error ? err.message : "Unable to resend invite." });
    } finally {
      setResendingId(null);
    }
  };

  const copyLink = useCallback(
    async (url: string) => {
      if (typeof navigator === "undefined" || !navigator.clipboard) {
        push({ title: "Copy failed", message: "Clipboard unavailable in this browser." });
        return;
      }
      const normalizedUrl = url.startsWith("/") ? `${window.location.origin}${url}` : url;
      try {
        await navigator.clipboard.writeText(normalizedUrl);
        push({ title: "Copied", message: "Invite link saved to clipboard." });
      } catch (err) {
        push({ title: "Copy failed", message: err instanceof Error ? err.message : "Clipboard unavailable." });
      }
    },
    [push],
  );

  const inviteSection = useMemo(() => {
    if (!lastUrl) return null;
    const displayUrl = lastUrl.startsWith("/") ? `${window.location.origin}${lastUrl}` : lastUrl;
    return (
      <div className="aa-glass-card rounded-2xl border border-primary/40 bg-primary/10 px-4 py-3 text-sm text-foreground">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="break-all">{displayUrl}</p>
          <Button size="sm" variant="ghost" onClick={() => void copyLink(lastUrl)}>
            Copy link
          </Button>
        </div>
      </div>
    );
  }, [lastUrl]);

  if (!isAdmin) {
    return (
      <div className="aa-page-shell space-y-4">
        <h1 className="text-2xl font-semibold text-foreground">Invites</h1>
        <p className="text-sm text-muted-foreground">Only admins and owners can manage invites.</p>
      </div>
    );
  }

  return (
    <div className="aa-page-shell space-y-6">
      <header>
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Master Control</p>
        <h1 className="text-3xl font-semibold text-foreground">Invite management</h1>
        <p className="text-sm text-muted-foreground">Generate invite links for new collaborators.</p>
      </header>
      <div className="flex flex-wrap gap-3 text-xs uppercase tracking-[0.3em] text-muted-foreground">
        <Link
          to="/control/master"
          className={
            isOverviewActive
              ? "rounded-full border border-primary/60 bg-primary/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground transition hover:bg-primary/20"
              : "rounded-full border border-transparent px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground/70 transition hover:border-border/40 hover:text-foreground"
          }
        >
          Overview
        </Link>
        <Link
          to="/control/master/invites"
          className={
            isInvitesActive
              ? "rounded-full border border-primary/60 bg-primary/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground transition hover:bg-primary/20"
              : "rounded-full border border-transparent px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground/70 transition hover:border-border/40 hover:text-foreground"
          }
        >
          Invites
        </Link>
        <Link
          to="/control/master/registry"
          className={
            isRegistryActive
              ? "rounded-full border border-primary/60 bg-primary/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground transition hover:bg-primary/20"
              : "rounded-full border border-transparent px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground/70 transition hover:border-border/40 hover:text-foreground"
          }
        >
          Registry
        </Link>
      </div>
      <form onSubmit={handleCreate} className="aa-glass-card grid gap-4 rounded-2xl p-6 sm:grid-cols-3">
        <div className="space-y-1 sm:col-span-2">
          <Label>Email address</Label>
          <Input
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            type="email"
            placeholder="new-agent@example.com"
            required
            className="w-full"
          />
        </div>
        <div className="space-y-1">
          <Label>Role</Label>
          <Select value={role} onValueChange={(value) => setRole(value as typeof role)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              {ROLE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Organization ID (optional)</Label>
            <Input
              value={orgId}
              onChange={(event) => setOrgId(event.target.value)}
              placeholder="org id (e.g., caltucky)"
              className="w-full"
            />
        </div>
        <div className="sm:col-span-3">
          <Button type="submit" className="w-full" disabled={creating}>
            {creating ? "Creating…" : "Create invite"}
          </Button>
          {inviteSection}
        </div>
      </form>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">Issued invites</h2>
          {loading && <p className="text-xs text-muted-foreground">Loading…</p>}
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Organization</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Accepted</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invites.map((invite) => (
              <TableRow key={invite.id}>
                <TableCell>{invite.email}</TableCell>
                <TableCell className="capitalize">{invite.role}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{invite.organization_id ?? "—"}</TableCell>
                <TableCell>{formatDate(invite.created_at)}</TableCell>
                <TableCell>{formatDate(invite.expires_at)}</TableCell>
                <TableCell>{invite.accepted_at ? formatDate(invite.accepted_at) : "Pending"}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void copyLink(invite.invite_url)}
                    >
                      Copy link
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={Boolean(invite.accepted_at) || Boolean(resendingId === invite.id)}
                      onClick={() => void handleResend(invite.id)}
                    >
                      {resendingId === invite.id ? "Resending…" : "Resend"}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!invites.length && (
              <TableRow>
                <TableCell colSpan={6} className="text-sm text-muted-foreground">
                  No invites issued yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </section>
    </div>
  );
}
